import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Github, Linkedin, Mail } from 'lucide-react';
import { personalInfo } from '../data/mock';

const Hero = () => {
  // 3-Beat Cinematic Sequence Timing
  const BREATH = 0.35; // 350ms breath after load
  const BEAT_1_START = BREATH; // Background: scale + fade
  const BEAT_1_DURATION = 1.2;
  const BEAT_2_START = BEAT_1_START + BEAT_1_DURATION * 0.7; // Headline overlap
  const BEAT_2_DURATION = 1.0;
  const BEAT_3_START = BEAT_2_START + BEAT_2_DURATION * 0.6; // CTA overlap
  const BEAT_3_DURATION = 0.8;

  // Container variants with stagger
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.08, // Subtle 80ms stagger between children
        delayChildren: BEAT_2_START
      }
    }
  };

  // Child variants for headline elements (mask reveal)
  const headlineChildVariants = {
    hidden: { y: 100 },
    visible: {
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  // Child variants for CTA elements (micro-overshoot)
  const ctaChildVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: BEAT_3_DURATION,
        ease: [0.34, 1.56, 0.64, 1], // Overshoot easing
        opacity: { duration: BEAT_3_DURATION * 0.5 }
      }
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* BEAT 1: Background establishes (scale + fade) */}
      <motion.div 
        className="absolute inset-0"
        initial={{ scale: 1.1, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ 
          duration: BEAT_1_DURATION, 
          delay: BEAT_1_START,
          ease: [0.22, 1, 0.36, 1]
        }}
      >
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url(/hero-temple-bg.jpg)' }}
        />
        
        {/* 50% Dark Overlay - Lighter */}
        <div className="absolute inset-0 bg-black/50" />
        
        {/* Additional atmospheric overlays with warmer tones */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/50" />
        <div className="absolute inset-0 bg-gradient-to-br from-amber-900/15 via-transparent to-orange-950/10" />
        
        {/* Mist overlay at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black to-transparent" />
      </motion.div>

      {/* BEAT 2 & 3: Content container with staggered children */}
      <motion.div 
        className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Badge */}
        <div style={{ overflow: 'hidden' }}>
          <motion.div variants={headlineChildVariants} className="mb-10">
            <span className="inline-block px-8 py-3 text-xs tracking-[0.5em] text-amber-400 border-2 border-amber-700/60 rounded-sm bg-black/50 backdrop-blur-md shadow-lg shadow-amber-900/20">
              THE WAY OF THE WARRIOR
            </span>
          </motion.div>
        </div>

        {/* Name */}
        <div style={{ overflow: 'hidden' }}>
          <motion.h1
            variants={headlineChildVariants}
            className="font-bebas text-7xl sm:text-8xl md:text-9xl lg:text-[11rem] tracking-wider text-white mb-8 leading-none drop-shadow-[0_0_30px_rgba(251,191,36,0.3)]"
          >
            {personalInfo.name.toUpperCase()}
          </motion.h1>
        </div>

        {/* Title with separator lines */}
        <div style={{ overflow: 'hidden' }}>
          <motion.div
            variants={headlineChildVariants}
            className="flex items-center justify-center gap-4 mb-10"
          >
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-600" />
            <p className="text-xl md:text-2xl text-amber-200 tracking-[0.3em] font-light">
              {personalInfo.title.toUpperCase()}
            </p>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-600" />
          </motion.div>
        </div>

        {/* Tagline */}
        <div style={{ overflow: 'hidden' }}>
          <motion.p
            variants={headlineChildVariants}
            className="text-base md:text-lg text-amber-100/80 tracking-wide mb-8 max-w-3xl mx-auto leading-relaxed font-light"
          >
            {personalInfo.tagline}
          </motion.p>
        </div>

        {/* Location (CTA with overshoot) */}
        <motion.div
          variants={ctaChildVariants}
          className="flex items-center justify-center gap-2 text-amber-100/70 mb-10"
        >
          <MapPin size={18} className="text-red-600" />
          <span className="text-base tracking-wider">{personalInfo.location}</span>
        </motion.div>

        {/* Social Links (CTA with overshoot) */}
        <motion.div
          variants={ctaChildVariants}
          className="flex items-center justify-center gap-8 mb-24"
        >
          {[
            { icon: Github, href: `https://${personalInfo.github}` },
            { icon: Linkedin, href: `https://${personalInfo.linkedin}` },
            { icon: Mail, href: `mailto:${personalInfo.email}` },
          ].map((item, i) => (
            <motion.a
              key={i}
              href={item.href}
              target={item.icon !== Mail ? "_blank" : undefined}
              rel="noopener noreferrer"
              className="group p-4 border-2 border-amber-800/50 rounded-full hover:border-amber-600/80 hover:bg-amber-900/20 transition-all duration-300 backdrop-blur-sm bg-black/30"
              whileHover={{ 
                boxShadow: '0 10px 30px -10px rgba(217, 119, 6, 0.5), 0 0 0 1px rgba(251, 191, 36, 0.2)',
                borderColor: 'rgba(217, 119, 6, 0.8)'
              }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.div
                whileHover={{ rotate: 5, scale: 1.1 }}
                transition={{ duration: 0.2 }}
              >
                <item.icon size={22} className="text-amber-200/70 group-hover:text-amber-100 transition-colors" />
              </motion.div>
            </motion.a>
          ))}
        </motion.div>

        {/* Space for katana - the 3D katana will be positioned here via ScrollingKatana */}
        <div className="h-[250px] mb-0" />
      </motion.div>
    </section>
  );
};

export default Hero;
